"use client";

import { Suspense } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Mail, Mountain } from "lucide-react";
import { usePageMetadata } from "@/lib/usePageMetadata";
import { PRODUCT_FEEDBACK_EMAIL } from "@/lib/feedback";

function AdventuresComingSoonContent() {
  const searchParams = useSearchParams();
  const adventure = searchParams.get("adventure");

  usePageMetadata({
    title: "Adventures Coming Soon — Pacific North North",
    description:
      "We're launching a local-first Alaska adventure marketplace. Learn about our mission and get in touch.",
    path: "adventures-coming-soon",
  });

  const contactMailto = `mailto:${PRODUCT_FEEDBACK_EMAIL}?subject=${encodeURIComponent(
    "Interested in Pacific North North adventures",
  )}&body=${encodeURIComponent(
    adventure
      ? `Hi PNN team,\n\nI was browsing "${adventure}" and would like to know when bookings open.\n\n`
      : "Hi PNN team,\n\nI'd like to learn more about adventures on Pacific North North.\n\n",
  )}`;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--pnn-cream)" }}>
      <section
        className="relative border-b overflow-hidden"
        style={{ borderColor: "rgba(180, 140, 45, 0.3)" }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(10, 55, 64, 0.92), rgba(10, 55, 64, 0.75))",
          }}
        />
        <div className="relative z-10 max-w-3xl mx-auto px-6 py-20 md:py-24 text-center space-y-6">
          <Compass className="h-14 w-14 mx-auto" style={{ color: "#D4AF37" }} />
          <h1
            className="text-3xl md:text-4xl font-bold tracking-tight"
            style={{ fontFamily: "var(--font-montserrat)", color: "#F8F5E9" }}
          >
            Adventures Coming Soon
          </h1>
          {adventure ? (
            <p className="text-lg" style={{ color: "rgba(248, 245, 233, 0.9)" }}>
              <span className="font-semibold">{adventure}</span> and more authentic Alaska
              experiences are on the way. We&apos;re onboarding local operators now.
            </p>
          ) : (
            <p className="text-lg" style={{ color: "rgba(248, 245, 233, 0.9)" }}>
              We&apos;re building a local-first marketplace for Alaska adventures — pilots,
              guides, charters, and outfitters serving the community.
            </p>
          )}
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-6 py-16 space-y-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border p-8 shadow-sm"
          style={{ borderColor: "rgba(10, 55, 64, 0.15)" }}
        >
          <Mountain className="h-10 w-10 mb-4" style={{ color: "#D4AF37" }} />
          <h2
            className="text-xl font-bold mb-3"
            style={{ color: "#0A3740", fontFamily: "var(--font-montserrat)" }}
          >
            Our mission
          </h2>
          <p className="text-sm leading-relaxed mb-4" style={{ color: "#243F42" }}>
            Pacific North North exists so travelers can book directly with local operators —
            built with regional operators. We&apos;re recruiting a small founding group of vendors to
            shape the product before we open bookings widely.
          </p>
          <Link
            href="/vision"
            className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
            style={{ color: "#0A3740" }}
          >
            Read our full vision
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl border p-8 shadow-sm text-center"
          style={{ borderColor: "rgba(10, 55, 64, 0.15)" }}
        >
          <Mail className="h-10 w-10 mx-auto mb-4" style={{ color: "#D4AF37" }} />
          <h2
            className="text-xl font-bold mb-3"
            style={{ color: "#0A3740", fontFamily: "var(--font-montserrat)" }}
          >
            Stay in the loop
          </h2>
          <p className="text-sm leading-relaxed mb-6" style={{ color: "#243F42" }}>
            Want to know when this adventure (or others) are bookable? Are you a local operator
            interested in listing? We&apos;d love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={contactMailto}
              className="px-6 py-3 rounded-lg font-semibold text-center transition-all hover:scale-[1.02]"
              style={{ backgroundColor: "#D4AF37", color: "#0A3740" }}
            >
              Contact Us
            </a>
            <Link
              href="/vendors/founding"
              className="px-6 py-3 rounded-lg font-semibold text-center border-2 transition-all hover:bg-white/80"
              style={{ borderColor: "#0A3740", color: "#0A3740" }}
            >
              Founding Vendor Program
            </Link>
          </div>
        </motion.div>

        <p className="text-center text-sm">
          <Link href="/" className="hover:underline" style={{ color: "#0A3740" }}>
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function AdventuresComingSoonPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F8F5E9" }}>
          <p style={{ color: "#0A3740" }}>Loading…</p>
        </div>
      }
    >
      <AdventuresComingSoonContent />
    </Suspense>
  );
}
